import React from "react";
import { Sparkle, CheckCircle, WarningCircle } from "@phosphor-icons/react";

/**
 * LLM-suggested field values shown as chips below the prompt input.
 *
 * Renders nothing when not suggesting and no suggestions to show.
 */
export default function SuggestionsPanel({ suggesting, suggestions, fieldValues, setFieldValues, setSuggestions }) {
  if (!suggesting && suggestions.length === 0) return null;

  const applyAll = () => {
    const next = { ...fieldValues };
    suggestions.forEach((s) => { if (s.validated !== "invalid") next[s.field] = s.value; });
    setFieldValues(next);
    setSuggestions([]);
  };

  const applyOne = (s) => {
    setFieldValues({ ...fieldValues, [s.field]: s.value });
    setSuggestions(suggestions.filter((x) => x.field !== s.field));
  };

  return (
    <div data-testid="agent-suggestions" className="rounded-lg border border-blue-200 bg-blue-50/40 px-3 py-2.5 -mt-1">
      <div className="flex items-center justify-between mb-1.5">
        <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-[#0020C2] flex items-center gap-1.5">
          <Sparkle size={11} weight="fill" /> Smart suggestions {suggesting && <span className="text-gray-400 font-normal normal-case">· thinking…</span>}
        </div>
        {suggestions.length > 1 && (
          <button data-testid="agent-suggestions-apply-all" onClick={applyAll}
            className="text-[10px] font-bold uppercase tracking-wider text-[#0020C2] hover:underline">
            Apply all
          </button>
        )}
      </div>
      {suggestions.length === 0 && !suggesting && (
        <div className="text-[11px] text-gray-500">No values to suggest yet.</div>
      )}
      <div className="flex flex-wrap gap-1.5">
        {suggestions.map((s) => {
          const tone = s.validated === "ok"
            ? "border-emerald-300 bg-emerald-50 text-emerald-700 hover:bg-emerald-100"
            : s.validated === "not_found" || s.validated === "invalid"
              ? "border-red-300 bg-red-50 text-[#DC2626] hover:bg-red-100"
              : "border-amber-300 bg-amber-50 text-amber-700 hover:bg-amber-100";
          return (
            <button key={s.field}
              data-testid={`agent-suggestion-${s.field}`}
              onClick={() => applyOne(s)}
              title={`Apply to ${s.label}`}
              className={`text-[11px] px-2 py-1 border rounded-md inline-flex items-center gap-1 ${tone}`}>
              <span className="font-bold uppercase tracking-wider">{s.label}:</span>
              <span className="font-mono">{s.value}</span>
              {s.validated === "ok" && <CheckCircle size={10} weight="fill" />}
              {(s.validated === "not_found" || s.validated === "invalid") && <WarningCircle size={10} weight="fill" />}
            </button>
          );
        })}
      </div>
    </div>
  );
}
